import {
  requestGetOfficeTransfer,
  requestPostConfirmOfficeTransfer,
  requestPostRefuseOfficeTransfer,
} from "@/state/officeTransfer/OfficeTransferEffects";
import { officeTransferStore as OTStore } from "@/state/officeTransfer/OfficeTransferStore";
import {
  OfficeTransferProofItem,
  OfficeTransferStore,
} from "@/type/core/officeTransfer/officeTransfer.types";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import PageLayout from "@components/core/layouts/PageLayout";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import CardSection from "@components/core/section/CardSection";
import BasicTable from "@components/core/tables/BasicTable";
import SeeMoreTableWrapper from "@components/core/tables/SeeMoreTableWrapper";
import { ROUTES } from "@routes/Routes";
import { formatPriceWithEuro } from "@utils/helpers/format-helper";
import { columns } from "@views/core/extranet/officeTransfer/confirmation/columns/OfficeTransferProofsColumns";
import { Col, Row } from "antd";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const OfficeTransferScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const params = useParams();
  const shortcut = params.shortcut;

  const officeTransferStore = useUnit<OfficeTransferStore | null>(OTStore);
  const sourceOfficeInfo =
    officeTransferStore?.officeTransferDetail?.sourceOfficeInfo;
  const targetOfficeInfo =
    officeTransferStore?.officeTransferDetail?.targetOfficeInfo;
  const userInfo = officeTransferStore?.officeTransferDetail?.userInfo;
  const sourceOfficeBalanceInCents =
    officeTransferStore?.officeTransferDetail?.sourceOfficeBalanceInCents;
  const managementFeesInCents =
    officeTransferStore?.officeTransferDetail?.managementFeesInCents;
  const transferAmountInCents =
    officeTransferStore?.officeTransferDetail?.transferAmountInCents;
  const currentTargetOfficeBalanceInCents =
    officeTransferStore?.officeTransferDetail
      ?.currentTargetOfficeBalanceInCents;
  const potentialTargetOfficeBalanceInCents =
    officeTransferStore?.officeTransferDetail
      ?.potentialTargetOfficeBalanceInCents;

  const [isLoading, setIsLoading] = useState(false);
  const [proofs, setProofs] = useState<OfficeTransferProofItem[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [isRefusalModalOpen, setIsRefusalModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (shortcut) {
      setIsLoading(true);
      void requestGetOfficeTransfer({ id: shortcut });
    }
  }, [shortcut]);

  useEffect(() => {
    return requestGetOfficeTransfer.doneData.watch((result) => {
      if (result.ok && result.data) {
        setProofs(result.data.proofs);
      } else {
        toastError(t("error.toast.retrieveDocuments"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestPostConfirmOfficeTransfer.doneData.watch((result) => {
      if (result.ok) {
        toastSuccess(t("success.confirmOfficeTransfer"));
      } else {
        toastError(t("error.toast.confirmOfficeTransfer"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestPostRefuseOfficeTransfer.doneData.watch((result) => {
      if (result.ok) {
        toastSuccess(t("success.declineOfficeTransfer"));
      } else {
        toastError(t("error.toast.declineOfficeTransfer"));
      }
      setIsLoading(false);
    });
  });

  const displayStringRow = (label: string, value: string) => {
    return (
      <>
        <Col span={24} md={6} />
        <Col span={24} md={6}>
          <strong>{t(`officeTransfer.${label}`)} :</strong>
        </Col>
        <Col span={24} md={12}>
          {value}
        </Col>
      </>
    );
  };

  const displayNumberRow = (label: string, value: number) => {
    const num = value / 100.0;
    return (
      <>
        <Col span={24} md={6} />
        <Col span={24} md={6}>
          <strong>{t(`officeTransfer.${label}`)} :</strong>
        </Col>
        <Col span={24} md={12}>
          {formatPriceWithEuro(num)}
        </Col>
      </>
    );
  };

  return (
    <PageLayout
      container
      isLoading={isLoading}
      returnRoute={ROUTES.extranet.home.generate()}
    >
      <CardSection title={t("officeTransfer.title")}>
        <div className="p-24">
          <Row
            gutter={30}
            className="border-bottom-secondary align-items-center py-24 mx-0"
          >
            <Col span={24} className="mb-8">
              <h2>{t("officeTransfer.information")}</h2>
            </Col>
            {sourceOfficeInfo &&
              displayStringRow("sourceOfficeInfo", sourceOfficeInfo)}
            {targetOfficeInfo &&
              displayStringRow("targetOfficeInfo", targetOfficeInfo)}
            {userInfo && displayStringRow("userInfo", userInfo)}
            {sourceOfficeBalanceInCents &&
              displayNumberRow(
                "sourceOfficeBalanceInCents",
                sourceOfficeBalanceInCents,
              )}
            {managementFeesInCents &&
              displayNumberRow("managementFeesInCents", managementFeesInCents)}
            {transferAmountInCents &&
              displayNumberRow("transferAmountInCents", transferAmountInCents)}
            {currentTargetOfficeBalanceInCents &&
              displayNumberRow(
                "currentTargetOfficeBalanceInCents",
                currentTargetOfficeBalanceInCents,
              )}
            {potentialTargetOfficeBalanceInCents &&
              displayNumberRow(
                "potentialTargetOfficeBalanceInCents",
                potentialTargetOfficeBalanceInCents,
              )}
          </Row>
          {proofs && proofs.length > 0 && (
            <Row
              gutter={30}
              className="border-bottom-secondary align-items-center py-24 mx-0"
            >
              <Col span={24} className="mb-8">
                <h2>{t("officeTransfer.proofs")}</h2>
              </Col>
              <Col span={24}>
                <SeeMoreTableWrapper
                  id="officeTransferProofTable"
                  showWrapper={proofs.length > 3}
                >
                  <BasicTable
                    id="officeTransferProofTable"
                    pagination={false}
                    columns={columns()}
                    dataSource={proofs}
                    className="table-no-header table-light-grey"
                  />
                </SeeMoreTableWrapper>
              </Col>
            </Row>
          )}
          <div className="d-flex align-items-center justify-content-end flex-wrap mt-8 gap-8">
            <BasicButton
              variant="tertiary"
              onClick={() => setIsRefusalModalOpen(true)}
              text={t("officeTransfer.refuse")}
            />
            <BasicButton
              onClick={() => setIsConfirmationModalOpen(true)}
              text={t("officeTransfer.confirm")}
            />
          </div>
          <ConfirmModal
            visible={isConfirmationModalOpen}
            onCancel={() => setIsConfirmationModalOpen(false)}
            onOk={() => {
              setIsLoading(true);
              setIsConfirmationModalOpen(false);
              if (shortcut) {
                void requestPostConfirmOfficeTransfer({
                  id: shortcut,
                });
              }
            }}
            title={t("officeTransfer.confirmationModal.title")}
            content={t("officeTransfer.confirmationModal.body")}
          />
          <ConfirmModal
            visible={isRefusalModalOpen}
            onCancel={() => setIsRefusalModalOpen(false)}
            onOk={() => {
              setIsLoading(true);
              setIsRefusalModalOpen(false);
              if (shortcut) {
                void requestPostRefuseOfficeTransfer({
                  id: shortcut,
                });
              }
            }}
            title={t("officeTransfer.refusalModal.title")}
            content={t("officeTransfer.refusalModal.body")}
          />
        </div>
      </CardSection>
    </PageLayout>
  );
};

export default OfficeTransferScreen;
